import React, { useCallback, useMemo } from "react";
import {
  Location,
  To,
  createPath,
  resolvePath,
  useLocation,
  useNavigate as useRouterNavigate,
} from "react-router-dom";

import { prefixOrgIdToUrl } from "../route/routes";

export function isModifiedEvent(event: React.MouseEvent): boolean {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

type ToType = To | ((location: Location) => To);

function resolveToLocation(to: ToType, currentLocation: Location): To {
  return typeof to === "function" ? to(currentLocation) : to;
}

export function useNavigate({
  replace,
  to,
}: Pick<UseOnClickArgs, "to" | "replace">): () => void {
  const navigate = useRouterNavigate();
  const location = useLocation();

  return useCallback(() => {
    const newLocation = resolveToLocation(to, location);
    if (replace) {
      navigate(newLocation, { replace: true });
    } else {
      navigate(newLocation);
    }
  }, [location, navigate, replace, to]);
}

export interface UseOnClickArgs {
  to: ToType;
  replace?: boolean | undefined;
  target?: string | undefined;
  /**
   * Additional logic to happen on click.
   * Called regardless of whether a navigation event happened or not.
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export function useOnClickProps({
  onClick: propOnClick,
  replace,
  target,
  to,
}: UseOnClickArgs): {
  target: string | undefined;
  href: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
} {
  const location = useLocation();

  const normalizedLocation = to
    ? resolvePath(resolveToLocation(to, location), location.pathname)
    : null;

  const href = normalizedLocation
    ? prefixOrgIdToUrl(createPath(normalizedLocation))
    : "";

  const navigate = useNavigate({ replace, to });

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      propOnClick?.(event);
      if (
        !event.defaultPrevented && // onClick prevented default
        event.button === 0 && // ignore everything but left clicks
        (!target || target === "_self") && // let browser handle "target=_blank" etc.
        !isModifiedEvent(event) // ignore clicks with modifier keys
      ) {
        event.preventDefault();
        navigate();
      }
    },
    [navigate, target, propOnClick],
  );

  return useMemo(
    () => ({
      target,
      href,
      onClick,
    }),
    [href, onClick, target],
  );
}
